import { OrbitsGame, NextObject, SimulationResult, isOrbit, getGridCenter, getOrbitwiseDirection } from './OrbitsGame.js';
import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    Edge,
    edgeKey,
    directionBetween
} from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, lerpColor, turnDelta } from '../../rendering/juice.js';

export interface OrbitsRenderConfig extends Partial<RenderConfig> {
    orbitColor?: string;
    orbitWidth?: number;
    gateColor?: string;
    gateWidth?: number;
    inactiveGateColor?: string;
    inactiveGateWidth?: number;
    blockedGateColor?: string;
    warningGateColor?: string;
    launcherColor?: string;
    launcherWrongColor?: string;
    pathColor?: string;
    pathWidth?: number;
    selectedPathColor?: string;
    correctBadgeColor?: string;
    incorrectBadgeColor?: string;
    digitBackgroundBorder?: string;
}

const ORBITS_DEFAULTS = {
    orbitColor: COLORS.darkGray,
    orbitWidth: 4,
    gateColor: COLORS.teal,
    gateWidth: 10,
    inactiveGateColor: COLORS.darkGray,
    inactiveGateWidth: 5,
    blockedGateColor: COLORS.gray,
    warningGateColor: COLORS.red,
    launcherColor: COLORS.teal,
    launcherWrongColor: COLORS.amber,
    pathColor: COLORS.blue,
    pathWidth: 3,
    selectedPathColor: COLORS.teal,
    correctBadgeColor: COLORS.teal,
    incorrectBadgeColor: COLORS.amber,
    digitBackgroundBorder: COLORS.borderGray
};

/** Unit vector for a cardinal direction in screen pixels (y grows downward). */
function cardinalUnit(dir: Direction): { x: number; y: number } {
    switch (dir) {
        case 'N': return { x: 0, y: -1 };
        case 'S': return { x: 0, y: 1 };
        case 'E': return { x: 1, y: 0 };
        case 'W': return { x: -1, y: 0 };
    }
}

/** Canvas arc angle (radians) for a cardinal direction (y grows downward). */
function cardinalAngle(dir: Direction): number {
    switch (dir) {
        case 'E': return 0;
        case 'S': return Math.PI / 2;
        case 'W': return Math.PI;
        case 'N': return -Math.PI / 2;
    }
}

// ── Launcher pose (docs/juice.md) ─────────────────────────────────────
//
// The launcher and its three gray hints are all the same object drawn four
// times: a triangle sitting on one of the four central posts, pointing along
// the orbit's tangent there. That makes the whole family two angles —
//
//   post     where round the centre it sits (a cardinal angle)
//   tangent  +90° if the orbit runs clockwise, -90° if it runs the other way;
//            the arrow points at `post + tangent`
//
// — both of which interpolate. Aiming at another edge sweeps `post` round the
// centre; flipping the orbit turns `tangent` through a half-circle, which
// rotates every arrow at once and flips the centre icon with them. The angles
// are kept *unwrapped* (never renormalised), so a click landing mid-tween
// retargets from wherever the launcher actually is.

interface LauncherPose {
    post: number;
    tangent: number;
}

interface LauncherAnim {
    from: LauncherPose;
    to: LauncherPose;
    start: number;
    fromDir: Direction;
    toDir: Direction;
}

interface LauncherFrame {
    pose: LauncherPose;
    /** Eased progress of the move, 1 once it has landed. */
    value: number;
    /** The post it left and the one it is heading for, while a move is running. */
    fromDir: Direction | null;
    toDir: Direction | null;
}

export class OrbitsRenderer extends GridRenderer {
    private orbitsConfig: typeof ORBITS_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state (docs/juice.md)
    private tweens = new TweenScheduler();
    private slotAnim: Map<string, number> = new Map();   // edgeKey -> tween start time
    private slotState: Map<string, boolean> = new Map();  // edgeKey -> last known active state
    private slotDismissed: Set<string> = new Set();  // retracting because a new gate displaced it
    private launcherPose: LauncherPose | null = null;   // where the launcher is at rest
    private launcherAnim: LauncherAnim | null = null;
    private launcherDir: Direction | null = null;       // last known post, null before the first frame
    private launcherClockwise: boolean = true;
    private launcherFrame: LauncherFrame | null = null; // this frame's pose, computed once
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: OrbitsGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: OrbitsRenderConfig = {}) {
        super(canvas, grid, config);
        this.orbitsConfig = { ...ORBITS_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every slot and the launcher at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.slotAnim.clear();
        this.launcherAnim = null;
    }

    render(game: OrbitsGame): void {
        const grid = game.getGrid();
        this.lastGame = game;
        this.animating = false;

        const now = this.tweens.now();
        this.syncLauncherTween(game, now);
        this.launcherFrame = this.launcherPoseAt(now);

        this.clear();
        this.drawGrid();
        this.drawActivePath(game);
        this.drawCornerHints(game);
        this.drawSlots(game);
        if (isVortexGame(game)) { this.drawActiveVortexGates(game); }
        this.drawInactiveLaunchers(game);
        this.drawLauncher(game);
        this.drawCenterIcon(game);

        // Draw digit and badges
        this.drawDigits(grid); // Always white
        this.drawBadges(game);

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    /**
     * Enumerate every orbit-edge gate slot (all concentric rings plus the rim).
     * These are exactly the edges for which isOrbit() is true.
     */
    private getSlotEdges(): Edge[] {
        const center = getGridCenter(this.grid.size);
        const maxDist = this.grid.size / 2;
        const edges: Edge[] = [];

        for (let dist = 1; dist <= maxDist; dist++) {
            const min = center.row - dist;
            const max = center.row + dist;

            // Top and bottom sides (horizontal edges)
            for (let c = min; c < max; c++) {
                const top = this.grid.createEdge({ row: min, col: c }, { row: min, col: c + 1 });
                const bottom = this.grid.createEdge({ row: max, col: c }, { row: max, col: c + 1 });
                if (top) edges.push(top);
                if (bottom) edges.push(bottom);
            }
            // Left and right sides (vertical edges)
            for (let r = min; r < max; r++) {
                const left = this.grid.createEdge({ row: r, col: min }, { row: r + 1, col: min });
                const right = this.grid.createEdge({ row: r, col: max }, { row: r + 1, col: max });
                if (left) edges.push(left);
                if (right) edges.push(right);
            }
        }

        return edges;
    }

    /**
     * Draw every gate slot. Each slot is a bar centered on its orbit edge: lying
     * *along* the line when inactive and rotated *across* the line when active. The
     * rotation is tweened so activating/deactivating animates. Slots next to digits
     * are drawn hollow (no gate may be placed there).
     */
    private drawSlots(game: OrbitsGame): void {
        const now = this.tweens.now();
        let animating = false;

        const dismissed = game.getDismissedGates();

        for (const edge of this.getSlotEdges()) {
            const key = edgeKey(edge);
            const active = game.hasGate(edge);
            const placeable = game.isGatePlaceable(edge);

            // Detect a state change since the last frame and start a tween. Every
            // slot is enumerated every frame, so `prev === undefined` only means
            // the first frame on this board: seed it, never replay it.
            const prev = this.slotState.get(key);
            if (this.tweens.isEnabled() && prev !== undefined && prev !== active) {
                this.slotAnim.set(key, now);
                // A gate thrown out by the placement just made retracts red; one the
                // player turned off themselves just fades back to grey.
                if (!active && dismissed.has(key)) this.slotDismissed.add(key);
                else this.slotDismissed.delete(key);
            }
            this.slotState.set(key, active);

            // `settle`: the bar is on the board either way, so this is a value
            // travelling between two resting states — no arrival, no overshoot.
            let progress = active ? 1 : 0;
            const animStart = this.slotAnim.get(key);
            if (animStart !== undefined) {
                const { value, done } = this.tweens.tween(animStart, MOTION.settle, now);
                progress = active ? value : 1 - value;
                if (done) {
                    this.slotAnim.delete(key);
                    this.slotDismissed.delete(key);
                } else {
                    animating = true;
                }
            }

            this.drawSlot(edge, progress, placeable, undefined, this.slotDismissed.has(key));
        }

        if (animating) this.animating = true;
    }

    /** Scoring overlay: redraw active gates (primary) in amber. */
    protected drawScoringPrimary(game: OrbitsGame): void {
        for (const key of game.getGates()) {
            const edge = this.grid.deserializeEdge(key);
            if (edge) this.drawSlot(edge, 1, true, COLORS.amber);
        }
    }

    private drawSlot(
        edge: Edge, progress: number, placeable: boolean, colorOverride?: string,
        dismissed: boolean = false
    ): void {
        const { cellSize } = this.config;
        const { gateColor, gateWidth, inactiveGateColor, inactiveGateWidth, blockedGateColor } = this.orbitsConfig;
        const { x, y } = this.getEdgePixelInfo(edge);

        // Inactive bar lies along the orbit line; active bar is rotated 90° across it.
        // Inactive bars nearly span the edge so consecutive slots join into the ring
        // outline; active gates shrink to a short bar across the line.
        const lineAngle = edge.orient === 'v' ? Math.PI / 2 : 0;
        const angle = lineAngle + progress * (Math.PI / 2);
        const inactiveLen = cellSize * 0.82;
        const activeLen = cellSize * 0.4;
        const half = (inactiveLen + (activeLen - inactiveLen) * progress) / 2;

        this.ctx.save();
        this.ctx.lineCap = 'round';
        this.ctx.globalAlpha = 1;
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);

        if (!placeable) {
            // Hollow slot: gate can't be placed here (adjacent to a digit).
            // Draw the dashes from the center outward so they stay symmetric.
            this.ctx.strokeStyle = blockedGateColor;
            this.ctx.lineWidth = inactiveGateWidth;
            this.ctx.setLineDash([2, 7]);
            this.ctx.beginPath();
            this.ctx.moveTo(0, 0);
            this.ctx.lineTo(half, 0);
            this.ctx.moveTo(0, 0);
            this.ctx.lineTo(-half, 0);
            this.ctx.stroke();
        } else {
            // A displaced gate retracts from red rather than teal: it reads as red at
            // full extension — the instant it is thrown out — then fades away as it
            // rotates back down to the line.
            const activeColor = dismissed ? COLORS.red : gateColor;
            this.ctx.strokeStyle = colorOverride ?? lerpColor(inactiveGateColor, activeColor, progress);
            this.ctx.lineWidth = inactiveGateWidth + (gateWidth - inactiveGateWidth) * progress;
            this.ctx.beginPath();
            this.ctx.moveTo(-half, 0);
            this.ctx.lineTo(half, 0);
            this.ctx.stroke();
        }

        this.ctx.restore();
    }

    /**
     * For each orbit-ring corner whose two perpendicular slots are both inactive,
     * draw a grey quarter-circle arrow showing the ship rounds that corner along
     * the orbit direction.
     */
    private drawCornerHints(game: OrbitsGame): void {
        const center = getGridCenter(this.grid.size);
        const maxDist = this.grid.size / 2;
        const clockwise = game.getLauncher().clockwise;

        // Cell rings live at distances 1 .. maxDist (the outermost, d=maxDist, is the
        // grid-corner ring whose corner slots are the rim/exit edges). Skip the
        // innermost ring (d=1, the central 2x2) — too crowded near the center.
        for (let d = 2; d <= maxDist; d++) {
            const min = center.row - d;
            const max = center.row + d;
            const corners: Array<{ cell: Position; h: Edge | null; v: Edge | null }> = [
                // NW
                { cell: { row: min, col: min }, h: this.grid.createEdge({ row: min, col: min }, { row: min, col: min + 1 }), v: this.grid.createEdge({ row: min, col: min }, { row: min + 1, col: min }) },
                // NE
                { cell: { row: min, col: max - 1 }, h: this.grid.createEdge({ row: min, col: max - 1 }, { row: min, col: max }), v: this.grid.createEdge({ row: min, col: max }, { row: min + 1, col: max }) },
                // SW
                { cell: { row: max - 1, col: min }, h: this.grid.createEdge({ row: max, col: min }, { row: max, col: min + 1 }), v: this.grid.createEdge({ row: max - 1, col: min }, { row: max, col: min }) },
                // SE
                { cell: { row: max - 1, col: max - 1 }, h: this.grid.createEdge({ row: max, col: max - 1 }, { row: max, col: max }), v: this.grid.createEdge({ row: max - 1, col: max }, { row: max, col: max }) }
            ];

            for (const cn of corners) {
                if (!cn.h || !cn.v) continue;
                if (game.hasGate(cn.h) || game.hasGate(cn.v)) continue;
                const arrowDir = getOrbitwiseDirection(cn.cell, clockwise, center);
                this.drawCornerArc(cn.cell, arrowDir);
            }
        }
    }

    private drawCornerArc(cell: Position, arrowDir: Direction): void {
        const { cellSize } = this.config;
        const center = getGridCenter(this.grid.size);
        const c = this.getCellCenter(cell);
        const r = cellSize * 0.3;

        // The two slots face the ring corner, i.e. the cell edges pointing away
        // from the grid center. Arc endpoints sit at those edge midpoints.
        const vDir: Direction = cell.row < center.row ? 'N' : 'S';
        const hDir: Direction = cell.col < center.col ? 'W' : 'E';
        const a1 = cardinalAngle(vDir);
        const a2 = cardinalAngle(hDir);

        this.ctx.save();
        this.ctx.strokeStyle = COLORS.gray;
        this.ctx.globalAlpha = 1;
        this.ctx.lineWidth = 3;
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([]);

        // Stroke the short (90°) arc between the two endpoints
        let delta = a2 - a1;
        while (delta <= -Math.PI) delta += 2 * Math.PI;
        while (delta > Math.PI) delta -= 2 * Math.PI;
        this.ctx.beginPath();
        this.ctx.arc(c.x, c.y, r, a1, a2, delta < 0);
        this.ctx.stroke();

        // Arrowhead at the downstream endpoint (the edge parallel to travel)
        const downstreamDir: Direction = (arrowDir === 'E' || arrowDir === 'W') ? vDir : hDir;
        const du = cardinalUnit(downstreamDir);
        // Pull the head slightly toward the cell center so the tip sits on the arc
        const tail = { x: c.x + du.x * r * 0.95, y: c.y + du.y * r * 0.95 };
        this.drawArrowHead(tail, arrowDir);

        this.ctx.restore();
    }

    private drawArrowHead(p: { x: number; y: number }, dir: Direction): void {
        const { cellSize } = this.config;
        const len = cellSize * 0.22;
        const wid = cellSize * 0.09;
        const u = cardinalUnit(dir);
        const perp = { x: -u.y, y: u.x };

        const tip = { x: p.x + u.x * len * 0.6, y: p.y + u.y * len * 0.6 };
        const back = { x: p.x - u.x * len * 0.4, y: p.y - u.y * len * 0.4 };

        this.ctx.beginPath();
        this.ctx.moveTo(tip.x, tip.y);
        this.ctx.lineTo(back.x + perp.x * wid, back.y + perp.y * wid);
        this.ctx.lineTo(back.x - perp.x * wid, back.y - perp.y * wid);
        this.ctx.closePath();
        this.ctx.fillStyle = COLORS.gray;
        this.ctx.fill();
    }

    /** Pixel position of the grid center intersection. */
    private getCenterPixel(): { x: number; y: number } {
        const { padding, cellSize } = this.config;
        const center = getGridCenter(this.grid.size);
        return { x: padding + center.col * cellSize, y: padding + center.row * cellSize };
    }

    /**
     * Draw the teal clockwise / counter-clockwise icon at the grid center: a circular
     * arrow whose sweep and arrowhead follow the current orbit direction. Clicking it
     * flips the direction.
     */
    private drawCenterIcon(_game: OrbitsGame): void {
        const { cellSize } = this.config;
        const { x: cx, y: cy } = this.getCenterPixel();
        const r = cellSize * 0.20;
        const gap = Math.PI * 0.45; // opening in the ring

        // Mirror horizontally for CCW: flips the gap to the other side and reverses
        // the arrow, so toggling the direction is clearly visible. Taken from the
        // launcher's tangent, so the icon turns over like a card as the arrows
        // swing round — +1 clockwise, -1 counter-clockwise, edge-on in between.
        const scaleX = this.launcherFrame!.pose.tangent / (Math.PI / 2);
        if (Math.abs(scaleX) < 0.02) return;   // edge-on: nothing to draw

        this.ctx.save();
        this.ctx.translate(cx, cy);
        this.ctx.scale(scaleX, 1);
        this.ctx.strokeStyle = this.orbitsConfig.gateColor;
        this.ctx.lineWidth = 3;
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([]);
        this.ctx.globalAlpha = 1;

        // Clockwise arc with the gap centered on the east side (local coords)
        this.ctx.beginPath();
        this.ctx.arc(0, 0, r, gap / 2, 2 * Math.PI - gap / 2, false);
        this.ctx.stroke();

        // Arrowhead at the leading (angle-increasing) end
        const e = 2 * Math.PI - gap / 2;
        const tx = -Math.sin(e);
        const ty = Math.cos(e);
        this.drawArrowTriangle(r * Math.cos(e), r * Math.sin(e), tx, ty, cellSize * 0.13);

        this.ctx.restore();
    }

    /** Filled triangle at (px,py) pointing along unit-ish vector (ux,uy). */
    private drawArrowTriangle(px: number, py: number, ux: number, uy: number, size: number): void {
        const len = Math.hypot(ux, uy) || 1;
        const nx = ux / len, ny = uy / len;
        const perp = { x: -ny, y: nx };
        const tip = { x: px + nx * size, y: py + ny * size };
        const base = { x: px - nx * size * 0.5, y: py - ny * size * 0.5 };

        this.ctx.beginPath();
        this.ctx.moveTo(tip.x, tip.y);
        this.ctx.lineTo(base.x + perp.x * size * 0.6, base.y + perp.y * size * 0.6);
        this.ctx.lineTo(base.x - perp.x * size * 0.6, base.y - perp.y * size * 0.6);
        this.ctx.closePath();
        this.ctx.fillStyle = this.orbitsConfig.gateColor;
        this.ctx.fill();
    }

    private getEdgePixelInfo(edge: Edge): { x: number; y: number; dx: number; dy: number } {
        const { padding, cellSize } = this.config;

        // Edge midpoint in pixel coordinates
        const x = padding + (edge.pa.col + edge.pb.col) / 2 * cellSize;
        const y = padding + (edge.pa.row + edge.pb.row) / 2 * cellSize;

        // Direction perpendicular to edge (for drawing the gate bar)
        // Vertical edges (same col) get horizontal bars, horizontal edges get vertical bars
        const dx = edge.orient === 'v' ? 1 : 0;
        const dy = edge.orient === 'h' ? 1 : 0;

        return { x, y, dx, dy };
    }

    /**
     * Pixel position of a launcher post. All four sit half a cell from the centre
     * intersection, so the post angle alone places it — and a post change can
     * sweep round that circle instead of jumping.
     */
    /** Where the launcher *belongs* right now, ignoring any tween. The ship's path
     *  is simulation, not action: it answers instantly and the arrow catches up. */
    private launcherRestPixel(game: OrbitsGame): { x: number; y: number } {
        return this.launcherPixel(cardinalAngle(game.getLauncher().direction));
    }

    private launcherPixel(postAngle: number): { x: number; y: number } {
        const { cellSize } = this.config;
        const { x: cx, y: cy } = this.getCenterPixel();
        const off = cellSize * 0.5;
        return { x: cx + Math.cos(postAngle) * off, y: cy + Math.sin(postAngle) * off };
    }

    /**
     * Start a launcher tween when the player aims at another central edge or
     * flips the orbit. Retargets from the launcher's *current* pose, so clicking
     * again mid-flight bends the motion rather than restarting it.
     */
    private syncLauncherTween(game: OrbitsGame, now: number): void {
        const { direction, clockwise } = game.getLauncher();
        const target: LauncherPose = {
            post: cardinalAngle(direction),
            tangent: clockwise ? Math.PI / 2 : -Math.PI / 2
        };

        // First frame on this board, or a static scene: adopt the pose, never
        // animate into it.
        if (this.launcherDir === null || !this.tweens.isEnabled()) {
            this.launcherPose = target;
            this.launcherAnim = null;
        } else if (direction !== this.launcherDir || clockwise !== this.launcherClockwise) {
            const from = this.launcherPoseAt(now).pose;
            const spin = clockwise ? 1 : -1;
            const to: LauncherPose = {
                post: from.post + turnDelta(from.post, target.post, spin),
                tangent: from.tangent + turnDelta(from.tangent, target.tangent, spin)
            };
            this.launcherAnim = {
                from, to, start: now, fromDir: this.launcherDir, toDir: direction
            };
            this.launcherPose = to;
        }

        this.launcherDir = direction;
        this.launcherClockwise = clockwise;
    }

    /** The launcher's pose this frame, and what it is moving between. */
    private launcherPoseAt(now: number): LauncherFrame {
        const rest = this.launcherPose ?? { post: 0, tangent: Math.PI / 2 };
        const anim = this.launcherAnim;
        if (!anim) return { pose: rest, value: 1, fromDir: null, toDir: null };

        // `change`: the launcher is already on the board and turns in place.
        const { value, done } = this.tweens.tween(anim.start, MOTION.change, now);
        if (done) {
            this.launcherAnim = null;
            return { pose: rest, value: 1, fromDir: null, toDir: null };
        }
        this.animating = true;
        return {
            pose: {
                post: anim.from.post + (anim.to.post - anim.from.post) * value,
                tangent: anim.from.tangent + (anim.to.tangent - anim.from.tangent) * value
            },
            value,
            fromDir: anim.fromDir,
            toDir: anim.toDir
        };
    }

    /**
     * Hint the three launch directions the launcher is *not* currently using: a
     * small gray triangle on each central edge, pointing in the direction the ship
     * would travel if launched that way (same idea as inactive gate slots).
     */
    private drawInactiveLaunchers(game: OrbitsGame): void {
        const { cellSize } = this.config;
        const launcher = game.getLauncher();
        const size = cellSize * 0.14;
        const dirs: Direction[] = ['N', 'E', 'S', 'W'];
        const frame = this.launcherFrame!;

        for (const d of dirs) {
            // While the launcher moves between two posts, the one it left fades
            // its hint back in and the one it is claiming fades out, so neither
            // pops under the arriving arrow.
            let alpha = 1;
            if (d === frame.fromDir) alpha = frame.value;
            else if (d === frame.toDir) alpha = 1 - frame.value;
            else if (d === launcher.direction) continue;
            if (alpha <= 0.01) continue;

            const { x, y } = this.launcherPixel(cardinalAngle(d));

            this.ctx.save();
            this.ctx.translate(x, y);
            this.ctx.rotate(cardinalAngle(d) + frame.pose.tangent);
            this.ctx.fillStyle = this.orbitsConfig.inactiveGateColor;
            this.ctx.globalAlpha = alpha;
            this.ctx.beginPath();
            this.ctx.moveTo(size, 0);
            this.ctx.lineTo(-size / 2, -size / 2);
            this.ctx.lineTo(-size / 2, size / 2);
            this.ctx.closePath();
            this.ctx.fill();
            this.ctx.restore();
        }
    }

    private drawLauncher(game: OrbitsGame): void {
        const { cellSize } = this.config;
        const { launcherColor, launcherWrongColor } = this.orbitsConfig;

        const { pose } = this.launcherFrame!;
        const { x, y } = this.launcherPixel(pose.post);

        // Determine color based on whether launcher hits the first digit
        const hitsFirst = game.getLauncherHitsFirst();
        const isCorrect = hitsFirst === this.grid.minDigit;
        const color = isCorrect ? launcherColor : launcherWrongColor;

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(pose.post + pose.tangent);

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        const arrowSize = cellSize * 0.25;
        this.ctx.moveTo(arrowSize, 0);
        this.ctx.lineTo(-arrowSize / 2, -arrowSize / 2);
        this.ctx.lineTo(-arrowSize / 2, arrowSize / 2);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.restore();
    }

    /** The path currently shown: from the selected digit, or from the launcher. */
    private activePath(game: OrbitsGame): SimulationResult & { fromLauncher: boolean } {
        const selectedDigit = game.getSelectedDigit();
        const fromLauncher = selectedDigit === null;
        const result = fromLauncher
            ? game.getPathFromLauncher()
            : game.getPathFromDigit(selectedDigit);
        return { ...result, fromLauncher };
    }

    private drawActivePath(game: OrbitsGame): void {
        const { padding, cellSize } = this.config;

        const result = this.activePath(game);
        const fromLauncher = result.fromLauncher;

        if (result.path.length < 1) return;

        const { pathColor, pathWidth } = this.orbitsConfig;

        this.ctx.strokeStyle = pathColor;
        this.ctx.lineWidth = pathWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.globalAlpha = 0.6;

        this.ctx.beginPath();

        // If from launcher, start at the launcher position (center edge)
        if (fromLauncher) {
            const { x: centerX, y: centerY } = this.launcherRestPixel(game);
            this.ctx.moveTo(centerX, centerY);
            const launchCellCenter = this.getCellCenter(result.path[0]);
            this.ctx.lineTo(launchCellCenter.x, launchCellCenter.y);
        } else {
            const firstCenter = this.getCellCenter(result.path[0]);
            this.ctx.moveTo(firstCenter.x, firstCenter.y);
        }

        const teleports = result.teleportIndices ? new Set(result.teleportIndices) : null;
        const teleportSegments: Array<{ from: Position; to: Position }> = [];

        for (let i = 1; i < result.path.length; i++) {
            if (teleports?.has(i)) {
                // Break the path line, record teleport for dashed line
                this.ctx.stroke();
                teleportSegments.push({ from: result.path[i - 1], to: result.path[i] });
                this.ctx.beginPath();
                const center = this.getCellCenter(result.path[i]);
                this.ctx.moveTo(center.x, center.y);
            } else if (i === result.path.length - 1 && result.hit?.type === 'launcher') {
                // Stop at the launcher edge, not the launcher cell center
                const { x, y } = this.launcherRestPixel(game);
                this.ctx.lineTo(x, y);
            } else {
                const center = this.getCellCenter(result.path[i]);
                this.ctx.lineTo(center.x, center.y);
            }
        }

        // If path exits, draw the half-edge to the boundary
        if (result.exitPoint) {
            const lastCell = result.path[result.path.length - 1];
            const exitPos = result.exitPoint;

            // Compute boundary pixel position based on exit direction
            let exitX: number, exitY: number;
            if (exitPos.row < 0) {
                // Exited north
                exitX = padding + (lastCell.col + 0.5) * cellSize;
                exitY = padding;
            } else if (exitPos.row >= this.grid.size) {
                // Exited south
                exitX = padding + (lastCell.col + 0.5) * cellSize;
                exitY = padding + this.grid.size * cellSize;
            } else if (exitPos.col < 0) {
                // Exited west
                exitX = padding;
                exitY = padding + (lastCell.row + 0.5) * cellSize;
            } else {
                // Exited east
                exitX = padding + this.grid.size * cellSize;
                exitY = padding + (lastCell.row + 0.5) * cellSize;
            }

            this.ctx.lineTo(exitX, exitY);
        }

        this.ctx.stroke();

        // Draw dashed purple teleport lines
        if (teleportSegments.length > 0) {
            this.ctx.strokeStyle = COLORS.purple;
            this.ctx.lineWidth = 3;
            this.ctx.setLineDash([6, 4]);
            this.ctx.lineCap = 'round';

            for (const seg of teleportSegments) {
                const from = this.getCellCenter(seg.from);
                const to = this.getCellCenter(seg.to);
                this.ctx.beginPath();
                this.ctx.moveTo(from.x, from.y);
                this.ctx.lineTo(to.x, to.y);
                this.ctx.stroke();
            }

            this.ctx.setLineDash([]);
        }

        this.ctx.globalAlpha = 1.0;

        this.drawDigitPathArrows(game, result, teleports);
    }


    /**
     * Blue triangles wherever the shown path enters or leaves a digit cell: an
     * inbound one on the edge it arrives across, an outbound one on the edge it
     * departs. They pick the ship's passage through each digit out of the orbit.
     */
    private drawDigitPathArrows(
        game: OrbitsGame, result: SimulationResult, teleports: Set<number> | null
    ): void {
        const grid = game.getGrid();
        const { path } = result;

        for (let i = 0; i < path.length; i++) {
            if (!grid.hasDigitAt(path[i])) continue;

            // Arrival edge — skip when the ship teleported into this cell (no edge crossed).
            if (i > 0 && !teleports?.has(i)) {
                const inDir = directionBetween(path[i - 1], path[i]);
                if (inDir) this.drawEdgeTriangle(path[i], inDir, this.orbitsConfig.pathColor, true);
            }

            // Departure edge — skip when the next step is a teleport out.
            if (i + 1 < path.length && !teleports?.has(i + 1)) {
                const outDir = directionBetween(path[i], path[i + 1]);
                if (outDir) this.drawEdgeTriangle(path[i], outDir, this.orbitsConfig.pathColor, false);
            }
        }
    }

    private drawBadges(game: OrbitsGame): void {
        const grid = game.getGrid();
        const { correctBadgeColor, incorrectBadgeColor } = this.orbitsConfig;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = grid.getDigitPosition(digit);
            if (!pos) continue;

            const next = game.getNextFromDigit(digit);

            // Determine expected next
            const expectedNext = digit < this.grid.maxDigit ? digit + 1 : null; // After max digit, expect exit
            const isCorrect = this.isNextCorrect(next, expectedNext);

            const label = this.getNextLabel(next);
            this.drawBadge(pos, 'SE', label, isCorrect ? correctBadgeColor : incorrectBadgeColor, "sm", true);
        }
    }

    private isNextCorrect(next: NextObject, expectedNext: number | null): boolean {
        if (next === null) return false;

        if (expectedNext === null) {
            // After digit 9, we expect exit
            return next.type === 'exit';
        }

        // Otherwise we expect the next digit
        return next.type === 'digit' && next.digit === expectedNext;
    }

    private getNextLabel(next: NextObject): string {
        if (next === null) return '?';
        if (next.type === 'digit') return next.digit.toString();
        if (next.type === 'exit') return 'E';
        if (next.type === 'launcher') return 'L';
        return '?';
    }

    // Get orbit edge from pixel (for gate placement)
    getOrbitEdgeFromPixel(x: number, y: number): Edge | null {
        const { padding, cellSize } = this.config;
        const gridX = (x - padding) / cellSize;
        const gridY = (y - padding) / cellSize;
        const threshold = 0.3;

        // Check horizontal edges (near integer row values)
        const nearestRow = Math.round(gridY);
        if (Math.abs(gridY - nearestRow) < threshold && nearestRow >= 0 && nearestRow <= this.grid.size) {
            const col1 = Math.floor(gridX);
            const col2 = col1 + 1;
            if (col1 >= 0 && col2 <= this.grid.size) {
                const edge = this.grid.createEdge({ row: nearestRow, col: col1 }, { row: nearestRow, col: col2 });
                if (edge && isOrbit(edge, this.grid.size)) return edge;
            }
        }

        // Check vertical edges (near integer col values)
        const nearestCol = Math.round(gridX);
        if (Math.abs(gridX - nearestCol) < threshold && nearestCol >= 0 && nearestCol <= this.grid.size) {
            const row1 = Math.floor(gridY);
            const row2 = row1 + 1;
            if (row1 >= 0 && row2 <= this.grid.size) {
                const edge = this.grid.createEdge({ row: row1, col: nearestCol }, { row: row2, col: nearestCol });
                if (edge && isOrbit(edge, this.grid.size)) return edge;
            }
        }

        return null;
    }
}
